<!--
 - Manage assignments component HTML
 -
 - @author Ryan Sweeney <rsweeney@buildcentrix.com>
 - @copyright (c) 2024 Build Centrix
 -->
<div class="manage-assignments">
  <bc-templates-modal-layout
    [isLoading]="isLoading"
    [loadingText]="('actions.loading'|translate)"
    (onCloseButtonClicked)="onDismiss()"
  >
    <!--Dialog Title-->
    <div title-content>
      {{(isEditMode?'actions.editType':'actions.addType')|translate: { type:
      ('shipping.driver.assignment.title'|translate) } }}
    </div>
    <!--Dialog Content-->
    <div body-content>
      <form #assignmentsForm="ngForm">
        <div *ngIf="!isLoading">
          <!--Date Start-->
          <div class="col-xs-12">
            <bc-atoms-input-group
              labelIcon="calendar"
              labelTitle="{{'shipping.driver.assignment.dateStart.title'|translate}}"
              labelDescription="{{'shipping.driver.assignment.dateStart.description'|translate}}"
              [required]="true"
            >
              <bc-atoms-date-picker
                id="dateStart"
                name="dateStart"
                placeholder="{{'shipping.driver.assignment.dateStart.title'|translate}}"
                title="{{'shipping.driver.assignment.dateStart.description'|translate}}"
                size="small"
                [max]="assignment.dateEnd"
                [required]="true"
                [disabled]="isSaving"
                [dateFormat]="wdEnvironmentService.dateFormat"
                [(model)]="assignment.dateStart"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-date-picker>
            </bc-atoms-input-group>
          </div>
          <!--Date End-->
          <div class="col-xs-12">
            <bc-atoms-input-group
              labelIcon="calendar"
              labelTitle="{{'shipping.driver.assignment.dateEnd.title'|translate}}"
              labelDescription="{{'shipping.driver.assignment.dateEnd.description'|translate}}"
              [required]="true"
            >
              <bc-atoms-date-picker
                id="dateEnd"
                name="dateEnd"
                placeholder="{{'shipping.driver.assignment.dateEnd.title'|translate}}"
                title="{{'shipping.driver.assignment.dateEnd.description'|translate}}"
                size="small"
                [min]="assignment.dateStart"
                [required]="true"
                [disabled]="isSaving"
                [dateFormat]="wdEnvironmentService.dateFormat"
                [(model)]="assignment.dateEnd"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-date-picker>
            </bc-atoms-input-group>
          </div>
          <!--Vehicle-->
          <div class="col-xs-12">
            <bc-atoms-input-group
              labelIcon="shippingVehicle"
              labelTitle="{{'shipping.driver.assignment.vehicle.title'|translate}}"
              labelDescription="{{'shipping.driver.assignment.vehicle.description'|translate}}"
              [required]="(wdVehicleAssignmentService.vehicleAssignmentRequiredData | async)?.vehicleOptions.length > 0"
              [state]="vehicleControl.status"
              [stateInput]="vehicleControl.errors"
              [stateTrigger]="vehicleControl.invalid && (vehicleControl.touched && vehicleControl.dirty)"
            >
              <bc-atoms-typeahead
                id="vehicle"
                name="vehicle"
                title="{{'shipping.driver.assignment.vehicle.description'|translate}}"
                placeholder="{{'shipping.driver.assignment.vehicle.title'|translate}}"
                labelKey="label"
                size="small"
                [required]="(wdVehicleAssignmentService.vehicleAssignmentRequiredData | async)?.vehicleOptions.length > 0"
                [disabled]="isSaving || (wdVehicleAssignmentService.vehicleAssignmentRequiredData | async)?.vehicleOptions.length === 0"
                [options]="(wdVehicleAssignmentService.vehicleAssignmentRequiredData | async)?.vehicleOptions"
                [(model)]="assignment.vehicle"
                [(ngModel)]="assignment.vehicle"
                #vehicleControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-typeahead>
            </bc-atoms-input-group>
          </div>
          <!--Driver-->
          <div class="col-xs-12">
            <bc-atoms-input-group
              labelIcon="user"
              labelTitle="{{'shipping.driver.assignment.driver.title'|translate}}"
              labelDescription="{{'shipping.driver.assignment.driver.description'|translate}}"
              [required]="false"
              [state]="driverControl.status"
              [stateInput]="driverControl.errors"
              [stateTrigger]="driverControl.invalid && (driverControl.touched && driverControl.dirty)"
            >
              <bc-atoms-typeahead
                id="driver"
                name="driver"
                title="{{'shipping.driver.assignment.driver.description'|translate}}"
                placeholder="{{'shipping.driver.assignment.driver.title'|translate}}"
                labelKey="fullName"
                size="small"
                [required]="false"
                [disabled]="isSaving || (wdVehicleAssignmentService.vehicleAssignmentRequiredData | async)?.userOptions.length === 0"
                [options]="(wdVehicleAssignmentService.vehicleAssignmentRequiredData | async)?.userOptions"
                [(model)]="assignment.driver"
                [(ngModel)]="assignment.driver"
                #driverControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-typeahead>
            </bc-atoms-input-group>
          </div>
          <!--Notes-->
          <div class="col-xs-12">
            <bc-atoms-input-group
              labelIcon="notifications"
              labelTitle="{{'shipping.driver.assignment.notes.title'|translate}}"
              labelDescription="{{'shipping.driver.assignment.notes.description'|translate}}"
              [required]="false"
              [state]="notesControl.status"
              [stateInput]="notesControl.errors"
              [stateTrigger]="notesControl.invalid && (notesControl.touched && notesControl.dirty)"
            >
              <bc-atoms-textarea
                id="descriptionInstructions"
                name="descriptionInstructions"
                placeholder="{{'shipping.driver.assignment.notes.title'|translate}}"
                title="{{'shipping.driver.assignment.notes.description'|translate}}"
                size="small"
                [required]="false"
                [disabled]="isSaving"
                [rows]="2"
                [(model)]="assignment.notes"
                [(ngModel)]="assignment.notes"
                #notesControl="ngModel"
                (modelChange)="checkForChanges()"
              >
              </bc-atoms-textarea>
            </bc-atoms-input-group>
          </div>
        </div>
      </form>
    </div>
    <!--Dialog Actions-->
    <div footer-content>
      <div class="buttons-container">
        <div class="right-buttons">
          <!--Cancel/Close -->
          <bc-atoms-button
            [label]="('actions.cancel'|translate)"
            [title]="('actions.cancel'|translate)"
            [disabled]="isLoading"
            type="default"
            icon="cancel"
            display="block"
            size="small"
            (onButtonClicked)="onDismiss()"
          >
          </bc-atoms-button>
          <!--Save -->
          <bc-atoms-button
            [label]="((isEditMode?'actions.update':'actions.save')|translate)"
            [title]="((isEditMode?'actions.update':'actions.save')|translate)"
            [disabled]="isLoading || !isChanges || !assignmentsForm.form.valid"
            type="success"
            icon="save"
            display="block"
            size="small"
            (onButtonClicked)="onSubmit(assignment)"
          >
          </bc-atoms-button>
        </div>
      </div>
    </div>
  </bc-templates-modal-layout>
</div>
